if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', class SearchDrawer extends DrawerElement {
    get shouldAppendToBody() {
      return false;
    }

    get input() {
      return this.querySelector('input[type="search"]');
    }

    get focusElement() {
      return this.input;
    }
  });
}

if (!customElements.get('search-typed')) {
  customElements.define('search-typed', class SearchTyped extends HTMLElement {
    constructor() {
      super();
      Motion.inView(this, this.init.bind(this));
    }

    get startDelay() {
      return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
    }

    async init() {
      this.insertCursor();
      await this.start(this.getAttribute('data-first-text'), this.startDelay);
      setTimeout(async () => {
        await this.reset();
        await this.start(this.getAttribute('data-last-text'), 0);
      }, 600);
    }

    async start(text, delay) {
      this.innerHTML = text;
      await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
      this.cursor.classList.add('blink');
    }

    async reset() {
      this.cursor.classList.remove('blink');
      await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
    }

    insertCursor() {
      if (this.cursor) return;
      this.cursor = document.createElement('span');
      this.cursor.className = 'typed-cursor';
      this.cursor.setAttribute('aria-hidden', true);
      this.cursor.innerHTML = '|';
      this.parentElement.insertBefore(this.cursor, this.nextSibling);
    }
  });
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', class PredictiveSearch extends HTMLFormElement {
    constructor() {
      super();
      this.cachedMap = new Map();
      this.focusElement = this.input;
      this.resetButton.addEventListener('click', this.clear.bind(this));
      this.input.addEventListener('click', this.hideTypewriter.bind(this));
      this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    get input() {
      return this.querySelector('input[type="search"]');
    }

    get resetButton() {
      return this.querySelector('button[type="reset"]');
    }

    get resultsElement() {
      return document.getElementById(`PredictiveSearchResults-${theme.utils.sectionId(this)}`);
    }

    get typewriter() {
      return this.input.previousElementSibling;
    }

    hideTypewriter() {
      if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
        this.typewriter.setAttribute('hidden', '');
      }
    }

    onFocus(event) {
      if (!this.closest('.collection')) return;

      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) return;
      this.renderSection(this.buildUrl().toString(), event);
    }

    clear(event = null) {
      if (event) event.preventDefault();
      if (this.abortController) this.abortController.abort();
      this.input.value = '';
      this.input.focus();
      this.removeAttribute('results');
      this.removeAttribute('loading');
      this.removeAttribute('error');
    }

    getQuery() {
      return this.input.value.trim();
    }

    buildUrl() {
      const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);
      url.searchParams.set('q', this.getQuery());
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit'), 10) : 6);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', theme.utils.sectionId(this));
      return url;
    }

    onChange() {
      if (this.getQuery().length === 0) {
        this.clear();
        return;
      }

      this.hideTypewriter();
      this.renderSection(this.buildUrl().toString());
    }

    renderSection(url) {
      if (this.cachedMap.has(url)) {
        this.renderSectionFromCache(url);
      } else {
        this.renderSectionFromFetch(url);
      }
    }

    renderSectionFromCache(url) {
      this.renderSearchResults(this.cachedMap.get(url));
      this.removeAttribute('error');
      this.setAttribute('results', '');
    }

    renderSectionFromFetch(url) {
      if (this.abortController) this.abortController.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.setAttribute('loading', '');
      this.removeAttribute('error');

      fetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          this.renderSearchResults(responseText);
          this.cachedMap.set(url, responseText);
          this.setAttribute('results', '');
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.renderError();
        })
        .finally(() => {
          if (this.abortController === controller) this.removeAttribute('loading');
        });
    }

    renderSearchResults(responseText) {
      if (!this.resultsElement) return;
      const parsedResults = new DOMParser().parseFromString(responseText, 'text/html')
        .getElementById(this.resultsElement.id);
      if (!parsedResults) throw new Error('Predictive search results were missing');
      this.resultsElement.innerHTML = parsedResults.innerHTML;
    }

    renderError() {
      if (!this.resultsElement) return;

      const fallback = document.createElement('div');
      const message = document.createElement('p');
      const link = document.createElement('a');
      fallback.className = 'afv-search-error grid gap-4 w-full';
      message.textContent = 'Search is temporarily unavailable.';
      link.className = 'button button--primary w-full';
      link.href = `${theme.routes.search_url}?q=${encodeURIComponent(this.getQuery())}`;
      link.textContent = 'VIEW ALL RESULTS';
      fallback.append(message, link);
      this.resultsElement.replaceChildren(fallback);
      this.setAttribute('error', '');
      this.setAttribute('results', '');
    }
  }, { extends: 'form' });
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define('predictive-search-overlay', class PredictiveSearchOverlay extends OverlayElement {
    constructor() {
      super();
      this.addEventListener('click', this.onClick);
    }

    onClick() {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  });
}
